import React from "react";
import { observer } from "mobx-react";
import { Backdrop, Box, CircularProgress, Typography, Alert } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { EngineeringIcon } from "../../icons";
import { useRootStore } from "../../store/contexts";

/**
 * Capa visual del modo mantenimiento (ver MaintenanceStore para las señales).
 * - warned: banner superior no bloqueante con cuenta regresiva — el usuario
 *   puede terminar de guardar lo que tiene abierto.
 * - active: backdrop bloqueante por encima de todo (incluidos modals); la app
 *   se recarga sola cuando el server vuelve.
 */
export const MaintenanceOverlay = observer(() => {
  const theme = useTheme();
  const { maintenanceStore, langStore } = useRootStore();
  const [, forceTick] = React.useState(0);

  // Tick por segundo solo mientras hay countdown visible.
  React.useEffect(() => {
    if (maintenanceStore.status !== "warned" || !maintenanceStore.until) return;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [maintenanceStore.status, maintenanceStore.until]);

  if (maintenanceStore.status === "warned") {
    const secs = maintenanceStore.secondsRemaining;
    const countdown =
      secs === null
        ? ""
        : secs >= 60
          ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`
          : `0:${String(secs).padStart(2, "0")}`;
    return (
      <Alert
        severity="warning"
        icon={<EngineeringIcon />}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20000,
          borderRadius: 0,
          justifyContent: "center",
        }}
      >
        {maintenanceStore.message || langStore.maintenanceWarnMessage}
        {countdown ? ` (${countdown})` : ""}
      </Alert>
    );
  }

  return (
    <Backdrop
      open={maintenanceStore.status === "active"}
      sx={{
        zIndex: 20000,
        backgroundColor: alpha(theme.palette.common.black, 0.92),
        flexDirection: "column",
        color: theme.palette.common.white,
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 480, px: 3 }}>
        <EngineeringIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
          {langStore.maintenanceTitle}
        </Typography>
        <Typography sx={{ mb: 3, opacity: 0.85 }}>
          {maintenanceStore.message || langStore.maintenanceDefaultMessage}
        </Typography>
        <CircularProgress size={28} sx={{ color: theme.palette.common.white, mb: 1.5 }} />
        <Typography variant="body2" sx={{ opacity: 0.6 }}>
          {langStore.maintenanceRetrying}
        </Typography>
      </Box>
    </Backdrop>
  );
});
